import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  InstallationWithMetrics
} from "@/metrics/installationMetrics";
import { zonedTimeToUtc } from 'date-fns-tz'
import { get, PaginatedResponse } from "@/api/api";
import { DateRange } from "@/dateRanges/dateRanges";

const getEquivalentTimeInNYC = (start: Date) => {
  // metrics are stored in the database with UTC timestamps.
  // when we're looking at DAY/WEEK durations (midnight-midnight), the data has been pulled with midnight = 00:00 NYC time,
  // so if you query for midnight-midnight for the same day but in another timezone, that day won't be returned (because the start datetime is different!)
  // to get around this, we take the browser's local time and convert it to NYC time.
  // the downside of this is that data in insights may not correspond to the user's local timezone (so we add a visual warning)
  // the upside of this is that a DAY or WEEK span will have the same data for all users, and we only need to generate one per day (instead of one per timezone per day!)
  return zonedTimeToUtc(start, 'America/New_York');
};

const useInstallationMetrics = (
  organizationId: number,
  dateRange: DateRange,
  filter?: string
) => {
  const start = useMemo(() => dateRange.value.from(), [dateRange]);
  const end = useMemo(() => dateRange.value.to(), [dateRange]);

  const startTimeInNYC = getEquivalentTimeInNYC(start);
  const endTimeInNYC = getEquivalentTimeInNYC(end);

  const [sorting, setSorting] = React.useState([{ id: "name", desc: false }]);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const page = pagination.pageIndex + 1

  const fetchUrl = "/metrics/"

  const buildUrl = (baseUrl: string): string => {
    let url = baseUrl
    url += `?organization_id=${organizationId}`
    url += `&page=${page}`
    url += `&page_size=${pagination.pageSize}`
    url += `&start=${startTimeInNYC.toISOString()}`
    url += `&end=${endTimeInNYC.toISOString()}`

    if (sorting?.length) {
      url += `&ordering=${sorting[0]?.desc ? "-" : ""}${sorting[0]?.id}`;
    }
    if (filter) {
      url += "&search=" + filter;
    }
    return url
  }

  const queryKey = ["installation-metrics", organizationId, page, sorting, start, end, filter];

  const installationMetrics = useQuery({
    queryKey: queryKey,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!organizationId) return null;
      return await get<PaginatedResponse<InstallationWithMetrics>>(buildUrl(fetchUrl));
    },
  });

  return { installationMetrics, sorting, setSorting, pagination, setPagination }
}

export default useInstallationMetrics;
